import Calculator from './Calculator'

export default class Residential extends Calculator {
  // Interface properties/methods

  getTotalForAverage() {
    // In residential developments, the total unit count is used for the total (as the average is per unit).
    return this.getTotalUnitCount()
  }

  getRawTotalArea () {
    return this.getValue('netLandArea')
  }

  getRawTotalUnitSize () {
    return this.getRawTotalPrivateUnitSize() + this.getRawTotalAffordableUnitSize()
  }

  // Private/Affordable totals

  getRawTotalPrivateUnitSize() {
    return this.getValue('unitCountPrivate') * this.getValue('unitSizePrivate')
  }

  getRawTotalAffordableUnitSize() {
    return this.getValue('unitCountAffordable') * this.getValue('unitSizeAffordable')
  }

  getTotalForPrivateArea (field) {
    return this.getRawTotalPrivateUnitSize() * this.getValue(field)
  }

  getTotalForAffordableArea (field) {
    return this.getRawTotalAffordableUnitSize() * this.getValue(field)
  }

  getTotalUnitCount () {
    return this.getValue('unitCountPrivate') + this.getValue('unitCountAffordable')
  }

  getPrivateAverage (value) {
    return this.getAverage(value, this.getValue('unitCountPrivate'))
  }
  getAffordableAverage (value) {
    return this.getAverage(value, this.getValue('unitCountAffordable'))
  }

  // Totals

  getGrossDevelopmentValue () {
    return this.getSalesValueTotal()
  }

  getBuildingCosts () {
    return (
      this.getBaseBuildCostTotal() +
      this.getDevelopmentContingencyTotal() +
      this.getConstructionAbnormalsTotal() +
      this.getExternalsTotal() +
      this.getProfessionalFeesTotal() +
      this.getSalesAndMarketingTotal() +
      this.getLegalFeesTotal() +
      this.getFinanceTotal()
    )
  }

  getProfitAndLandValueCosts () {
    return (
      this.getDeveloperProfitTotal() +
      this.getLegalSiteAcquisitionCostTotal() +
      this.getPlanningTotal() +
      this.getSurveysTotal()
    )
  }

  // 01 - Gross Development Value

  getDensity (large = false) {
    return this.getTotalUnitCount() / this.getTotalArea(large)
  }

  getSalesValueTotal () {
    return this.getPrivateSalesValueTotal() + this.getAffordableSalesValueTotal()
  }

  getTotalPrivateUnitSize (large = false) {
    return this.translateArea(this.getRawTotalPrivateUnitSize(), large)
  }

  getPrivateSalesValueTotal () {
    return this.getTotalForPrivateArea('salesValuePrivate')
  }

  getTotalAffordableUnitSize (large = false) {
    return this.translateArea(this.getRawTotalAffordableUnitSize(), large)
  }

  getAffordableSalesValueTotal () {
    return this.getTotalForAffordableArea('salesValueAffordable')
  }


  // 02 - Costs

  getBaseBuildCostTotal () {
    return this.getTotalForArea('baseBuildCost')
  }

  getDevelopmentContingencyTotal () {
    return this.getPercentageOrFixedValue('developmentContingency', this.getBaseBuildCostTotal())
  }

  getConstructionAbnormalsTotal () {
    return this.getValue('constructionAbnormals')
  }

  getExternalsTotal () {
    return this.getPercentageOrFixedValue('externals', this.getBaseBuildCostTotal())
  }

  getProfessionalFeesTotal () {
    const costsSubTotal = (
      this.getBaseBuildCostTotal() +
      this.getDevelopmentContingencyTotal() +
      this.getConstructionAbnormalsTotal() +
      this.getExternalsTotal()
    )

    return this.getPercentageOrFixedValue('professionalFees', costsSubTotal)
  }

  getSalesAndMarketingTotal () {
    return this.getPercentageOrFixedValue('salesAndMarketing', this.getGrossDevelopmentValue())
  }

  getLegalFeesTotal () {
    return this.getValue('legalFeesPerUnit') * this.getTotalUnitCount()
  }

  getFinanceTotal () {
    const costsSubTotal = (
      this.getBaseBuildCostTotal() +
      this.getDevelopmentContingencyTotal() +
      this.getConstructionAbnormalsTotal() +
      this.getExternalsTotal() +
      this.getProfessionalFeesTotal() +
      this.getSalesAndMarketingTotal() +
      this.getLegalFeesTotal()
    )

    return this.getPercentageOrFixedValue('finance', costsSubTotal)
  }

  // 03 - Profit

  getDeveloperProfitTotal () {
    let total

    if (this.data.profit.unit === 'percentgdv') {
      total =  this.getGrossDevelopmentValue()
    } else if (this.data.profit.unit === 'percentcost') {
      total = this.getBuildingCosts()
    }

    return this.getPercentageOrFixedValue('profit', total)
  }

  getLegalSiteAcquisitionCostTotal () {
    return this.getValue('legalSiteAcquisitionCost')
  }

  getPlanningTotal () {
    return this.getValue('planning')
  }

  getSurveysTotal () {
    return this.getValue('surveys')
  }

  // Translation

  translateToUnit (newUnit) {
    if (newUnit === 'imperial' && this.unit === 'metric') {
      return {
        ...this.data,
        unit: newUnit,

        netLandArea: this.data.netLandArea && this.hectaresToAcres(this.data.netLandArea),
        unitSize: this.data.unitSize && this.sqmToSqft(this.data.unitSize),
        salesValue: this.data.salesValue && this.psqmToPsqft(this.data.salesValue),
        baseBuildCost: this.data.baseBuildCost && this.psqmToPsqft(this.data.baseBuildCost),
      }
    } else if (newUnit === 'metric' && this.unit === 'imperial') {
      return {
        ...this.data,
        unit: newUnit,

        netLandArea: this.data.netLandArea && this.acresToHectares(this.data.netLandArea),
        unitSize: this.data.unitSize && this.sqftToSqm(this.data.unitSize),
        salesValue: this.data.salesValue && this.psqftToPsqm(this.data.salesValue),
        baseBuildCost: this.data.baseBuildCost && this.psqftToPsqm(this.data.baseBuildCost),
      }
    }

    return this.data
  }
}
